/**
 * Dropdown options for the product form's Category / Sub Category pickers.
 * The rows come from /category/list?business_id=<id>&include_platform=true:
 * Super Admin (marketplace) rows have no business_id, the store's own rows
 * have it. Names that differ only in case/spacing are one option, and the
 * Super Admin spelling / row wins.
 */

export const normaliseName = (name) => String(name || "").trim().replace(/\s+/g, " ").toLowerCase();

const isMarketplaceRow = (row) => row.business_id === null || row.business_id === undefined;

// Super Admin rows first, so they win the de-duplication.
const marketplaceFirst = (rows) =>
    [...rows].sort((a, b) => Number(!isMarketplaceRow(a)) - Number(!isMarketplaceRow(b)));

const byName = (a, b) => a.localeCompare(b, "en", { sensitivity: "base" });

/**
 * @param {Array<{category: string, business_id?: string|null}>} rows
 * @returns {{marketplace: string[], store: string[]}} category names, each list sorted by name
 */
export const buildCategoryOptions = (rows = []) => {
    const seen = new Set();
    const marketplace = [];
    const store = [];
    marketplaceFirst(rows).forEach((row) => {
        const name = String(row.category || "").trim();
        const key = normaliseName(name);
        if (!key || seen.has(key)) return;
        seen.add(key);
        (isMarketplaceRow(row) ? marketplace : store).push(name);
    });
    return { marketplace: marketplace.sort(byName), store: store.sort(byName) };
};

/**
 * @param {Array<{_id: string, subcategory: string, business_id?: string|null}>} rows - rows of one category
 * @param {string} parentCategory
 * @returns {Array<{id: string, subcategory: string, parentCategory: string, isMarketplace: boolean, inStore: boolean}>}
 *   inStore: the store has its own row for this name (alone, or alongside
 *   the marketplace one)
 */
export const buildSubcategoryOptions = (rows = [], parentCategory) => {
    const byKey = new Map();
    const options = [];
    marketplaceFirst(rows).forEach((row) => {
        const key = normaliseName(row.subcategory);
        if (!key) return;
        if (byKey.has(key)) {
            if (!isMarketplaceRow(row)) byKey.get(key).inStore = true;
            return;
        }
        const option = {
            id: row._id,
            subcategory: String(row.subcategory).trim(),
            parentCategory,
            isMarketplace: isMarketplaceRow(row),
            inStore: !isMarketplaceRow(row),
        };
        byKey.set(key, option);
        options.push(option);
    });
    return options.sort((a, b) => byName(a.subcategory, b.subcategory));
};
